import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  FaCheck,
  FaEdit,
  FaEye,
  FaMoneyBillWave,
  FaPrint,
  FaWalking,
} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../../PageHeading";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import ExportOptions from "../../ExportOptions";
import axios from "axios";
import { useAuth } from "../../../utils/AuthContext";
import TableSkeleton from "../../TableSkeleton";
import HospitalBillModal from "./HospitalBillModal";
import HospitalViewBillModal from "./HospitalViewBillModal";
import DynamicSelect from "../../DynamicSelect";
import SmartDropdown from "../../Shared/SmartDropdown";
import HospitalAddPaymentModal from "./HospitalAddPaymentModal";
import { PrintBillLayoutA5 } from "../../PrintLayoutA5";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const HospitalBill = () => {
  // State Definitions
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [viewBillModalOpen, setViewBillModalOpen] = useState(false);
  const [viewBillData, setViewBillData] = useState(null);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [editBillId, setEditBillId] = useState(null);
  const [editBillData, setEditBillData] = useState(null);
  const [bills, setBills] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Filter States
  const [filterType, setFilterType] = useState("all");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString(),
  );

  // Dropdown & Action States
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTriggerRefs = useRef({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentBillData, setPaymentBillData] = useState(null);
  const [releaseModalOpen, setReleaseModalOpen] = useState(false);
  const [billToRelease, setBillToRelease] = useState(null);

  const itemsPerPage = 20;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const actionButtonClass =
    "size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300";
  const { user } = useAuth();

  const filterOptions = [
    { value: "all", label: "All Time" },
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  // --- FETCH BILLS ---
  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const url = `https://api.muktihospital.com/api/hospital/hospital-bills`;

      const params = {
        page: 1,
        limit: 100000,
        search: debouncedSearchQuery,
        type: filterType,
      };

      if (filterType === "daily") {
        params.startDate = startDate;
        params.endDate = endDate;
      } else if (filterType === "monthly") {
        const [y, m] = selectedMonth.split("-");
        params.year = y;
        params.month = m;
      } else if (filterType === "yearly") {
        params.year = selectedYear;
      }

      const response = await axios.get(url, {
        headers: {
          "x-api-key":
            "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
        },
        params: params,
      });

      const { bills, total } = response.data;
      setBills(bills || []);
      setTotalRecords(total || 0);
      setTotalPages(Math.ceil((total || 0) / itemsPerPage) || 1);
    } catch (err) {
      console.error("Error fetching bills:", err);
      toast.error("Failed to load bills.");
      setBills([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    debouncedSearchQuery,
    itemsPerPage,
    filterType,
    startDate,
    endDate,
    selectedMonth,
    selectedYear,
  ]);

  // --- EFFECTS ---
  useEffect(() => {
    fetchBills();
  }, [fetchBills]);
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchQuery,
    filterType,
    startDate,
    endDate,
    selectedMonth,
    selectedYear,
  ]);

  const handleRefreshBills = useCallback(async () => {
    await fetchBills();
  }, [fetchBills]);


  const handleReleaseClick = (bill) => {
    const netAmount = parseFloat(bill.netAmount || 0);
    const paidAmount = parseFloat(bill.paidAmount || 0);

    if (paidAmount < netAmount - 0.01) {
      const due = (netAmount - paidAmount).toFixed(2);
      toast.warn(
        `Cannot release patient. Bill is not fully paid. Due: ৳${due}`,
      );
      return;
    }
    setBillToRelease(bill);
    setReleaseModalOpen(true);
  };

  const confirmRelease = async () => {
    if (!billToRelease) return;
    try {
      await axios.put(
        `https://api.muktihospital.com/api/hospital/hospital-bills/release/${billToRelease.id}`,
        {},
        {
          headers: {
            "x-api-key":
              "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        },
      );
      toast.success("Patient released successfully!");
      handleRefreshBills();
    } catch (error) {
      console.error("Release error:", error);
      toast.error("Failed to release patient.");
    } finally {
      setReleaseModalOpen(false);
      setBillToRelease(null);
    }
  };

  const handleAddMoney = (bill) => {
    setPaymentBillData(bill);
    setPaymentModalOpen(true);
  };

  // --- FILTERS & PAGINATION LOGIC ---
  const filteredBills = useMemo(() => {
    if (!debouncedSearchQuery) return bills;
    const q = debouncedSearchQuery.toLowerCase().trim();
    const onlyDigits = (s) => (s || "").replace(/\D/g, "");
    const qDigits = onlyDigits(q);

    // Reg No is written INSIDE patientName like "sharmin (HR-30)".
    const getRegNo = (bill) => {
      const name = bill.patientName || "";
      const m = name.match(/\(([^)]*)\)\s*$/);
      const inName = m ? m[1] : "";
      return (inName || bill.regNo || "").toLowerCase().trim();
    };

    const rank = (bill) => {
      const reg = getRegNo(bill); 
      const regDigits = onlyDigits(reg);
      if (reg === q) return 0;     
      if (qDigits && regDigits === qDigits) return 1;
      if (q && reg.includes(q)) return 2;
      if (qDigits && regDigits.includes(qDigits)) return 3;
      const name = (bill.patientName || "").toLowerCase();
      const billNo = (bill.billNumber || "").toLowerCase();
      if (name.includes(q) || billNo.includes(q)) return 4;
      return 5;
    };

    return [...bills].sort((a, b) => rank(a) - rank(b));
  }, [bills, debouncedSearchQuery]);

 const currentData = useMemo(() => {
      const start = (currentPage - 1) * itemsPerPage;
      return filteredBills.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, filteredBills]);

  useEffect(() => {
  setCurrentPage(1);
}, [
  debouncedSearchQuery, 
  filterType, 
  startDate, 
  endDate, 
  selectedMonth, 
  selectedYear
]);

  // --- CRUD HANDLERS ---
  const handleDelete = useCallback(
    async (billIds) => {
      try {
        await Promise.all(
          billIds.map((id) =>
            axios.delete(
              `https://api.muktihospital.com/api/hospital/hospital-bills/delete/${id}`,
              {
                headers: {
                  "x-api-key":
                    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
                },
              },
            ),
          ),
        );
        await handleRefreshBills();
        setSelectedRows([]);
        toast.success(`${billIds.length} bill(s) deleted successfully!`);
      } catch (err) {
        toast.error("Failed to delete bill(s).");
      }
    },
    [handleRefreshBills],
  );

  const handleBillUpdate = useCallback((updatedBill) => {
    setBills((prevBills) =>
      prevBills.map((bill) =>
        bill.id === updatedBill.id ? { ...bill, ...updatedBill } : bill
      )
    );
    setViewBillData((prev) =>
      prev && prev.id === updatedBill.id ? { ...prev, ...updatedBill } : prev
    );
  }, []);

 const handleEdit = useCallback(async (bill) => {
      try {
        if (!bill?.id) {
          toast.error("Invalid bill ID", { autoClose: 2000 });
          return;
        }
        
        const response = await axios.get(`https://api.muktihospital.com/api/hospital/hospital-bills/${bill.id}`, {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079",
          },
        });

        const fullBillData = response.data.bill || response.data;

        setEditBillId(fullBillData.id);
        setEditBillData(fullBillData);
        setGenerateBillModalOpen(true);

      } catch (err) {
        console.error("Error fetching bill for edit:", {
          message: err.message,
          response: err.response?.data,
        });
        toast.error(err.response?.data?.error || "Failed to load bill for editing.", {
          autoClose: 2000,
        });
      }
    }, []);

  const handleView = useCallback((bill) => {
    setViewBillData(bill);
    setViewBillModalOpen(true);
  }, []);

  const handlePrintBill = useCallback(async (bill) => {
    try {
      if (!bill?.id) { toast.error("Invalid bill ID"); return; }
      const response = await axios.get(`https://api.muktihospital.com/api/hospital/hospital-bills/${bill.id}`, {
        headers: { "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079" },
      });
      const b = response.data.bill || response.data;
      const services = b.serviceItems || [];
      const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "N/A");

      const signatureFooter = (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0px", paddingRight: "6px" }}>
          <div style={{ textAlign: "center", minWidth: "180px" }}>
            <p style={{ margin: "0 0 2px 0", fontWeight: "bold", fontSize: "9.5pt", color: "#323290", fontFamily: "cursive" }}>{b.authorizedBy || "N/A"}</p>
            <div style={{ borderTop: "1.5px solid #323290", width: "160px", margin: "0 auto 3px auto" }}></div>
            <p style={{ margin: "0", fontSize: "8pt", fontWeight: "bold", letterSpacing: "0.5px", textTransform: "uppercase", color: "#374151" }}>Authorized By</p>
            <p style={{ margin: "1px 0 0 0", fontSize: "7pt", color: "#6b7280" }}>Mukti Hospital Pvt. Ltd.</p>
          </div>
        </div>
      );

      const billInfo = (
        <>
          <div>
            <p><strong>Bill No:</strong> {b.billNumber || "N/A"}</p>
            <p><strong>Special No:</strong> {b.specialNo || "N/A"}</p>
            <p><strong>Reg No:</strong> {b.regNo || "N/A"}</p>
          </div>
          <div>
            <p><strong>Admission Date:</strong> {fmtDate(b.admissionDate)}</p>
            <p><strong>Billing Date:</strong> {fmtDate(b.billingDate)}</p>
          </div>
          <p className="full"><strong>Patient Name:</strong> {b.patientName || "N/A"}</p>
          <p className="full"><strong>Consultant:</strong> {b.consultantName || "N/A"}</p>
        </>
      );

      const table = (
        <table className="w-full border-collapse text-base">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border font-semibold">Sl No</th>
              <th className="p-2 border font-semibold">Service Name</th>
              <th className="p-2 border font-semibold text-right">Unit Price (৳)</th>
              <th className="p-2 border font-semibold text-right">Total Unit</th>
              <th className="p-2 border font-semibold text-right">Amount (৳)</th>
            </tr>
          </thead>
          <tbody>
            {services.map((row, index) => (
              <tr key={index} className="border-t">
                <td className="p-2 border">{index + 1}</td>
                <td className="p-2 border">{row.serviceName || "N/A"}</td>
                <td className="p-2 border text-right">{parseFloat(row.unitPrice || 0).toFixed(2)}</td>
                <td className="p-2 border text-right">{row.totalUnit || "0"}</td>
                <td className="p-2 border text-right">{parseFloat(row.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );

      const billAmount = services.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      const discount = Number(b.discountAmount ?? b.discount ?? 0);
      const netAmount = Number(b.netAmount || 0);
      const paidAmount = Number(b.paidAmount || 0);
      const payable = netAmount - paidAmount;

      const summary = (
        <div className="flex flex-col text-xl">
          <div className="flex justify-between text-gray-600"><span>Bill Amount:</span><span>{Math.round(billAmount).toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Discount:</span><span>{Math.round(discount).toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Net Amount:</span><span>{Math.round(netAmount).toFixed(2)}</span></div>
          <div className="flex justify-between text-gray-600"><span>Paid Amount:</span><span>{Math.round(paidAmount).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-xl">Payable Amount:</span><span className="text-xl">{Math.round(payable).toFixed(2)}</span></div>
        </div>
      );

      setPrintData({ billInfo, table, summary, signatureFooter, title: "Hospital Bill" });
      setShowPrintPreview(true);
    } catch (err) {
      console.error("Error printing bill:", err);
      toast.error("Failed to load bill for printing.");
    }
  }, []);

  useEffect(() => {
    if (showPrintPreview && printData) {
      const timer = setTimeout(() => {
        const elementsToHide = document.querySelectorAll(".print-hidden, .print-hidden-in-print");
        elementsToHide.forEach((el) => (el.style.display = "none"));
        const printLayout = document.querySelector(".hospital-bill-list-print-preview");
        if (printLayout) printLayout.style.display = "block";
        window.print();
        setTimeout(() => {
          elementsToHide.forEach((el) => (el.style.display = ""));
          if (printLayout) printLayout.style.display = "none";
          setShowPrintPreview(false);
          setPrintData(null);
        }, 500);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showPrintPreview, printData]);

   const handlePageChange = useCallback((page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedRows([]);
    }
  }, [totalPages]);

  const handleSelectAll = useCallback(
    (e) => {
      const validIds = currentData
        .filter((bill) => bill.id != null)
        .map((bill) => bill.id);
      setSelectedRows(e.target.checked ? validIds : []);
    },
    [currentData],
  );
  const handleRowSelect = useCallback((billId) => {
    if (billId == null) return;
    setSelectedRows((prev) =>
      prev.includes(billId)
        ? prev.filter((id) => id !== billId)
        : [...prev, billId],
    );
  }, []);
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);
  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "SL No",
    "BILL NO",
    "Patient Name",
    "DATE",
    "DISCOUNT (৳)",
    "NET AMOUNT (৳)",
    "PAID AMOUNT (৳)",
    "SUBMITTED BY",
    "AUTHORIZED BY",
  ];
  const skeletonColumns = [
    {
      width: 20,
      height: 20,
      isCircle: true,
      className: "print-hidden-in-print",
    },
    { width: 50, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 100, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 150, height: 16 },
    { width: 120, height: 16, className: "print-hidden-in-print" },
  ];

  const dataMapper = (bill) =>
    [
      (currentPage - 1) * itemsPerPage + bills.indexOf(bill) + 1,
      bill.billNumber || "-",
      bill.patientName || "",
      bill.billingDate 
        ? new Date(bill.billingDate).toLocaleDateString("en-GB")
        : "-",
      bill.discount?.toFixed(2) || "0.00",
      bill.netAmount?.toFixed(2) || "0.00",
      bill.paidAmount?.toFixed(2) || "0.00",
      user?.name || "Unknown",
    ].join("\t");

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "Hospital Office" },
      { label: "Hospital Bills" },
    ],
    [],
  );
  const allVisibleSelected = useMemo(
    () =>
      currentData.length > 0 &&
      currentData.every((bill) => bill.id && selectedRows.includes(bill.id)),
    [currentData, selectedRows],
  );

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Hospital Bills" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        {/* --- HEADER --- */}
        <div className="flex justify-between flex-wrap border-b border-dashed border-M-text-color/50 p-4 gap-4 print-hidden">
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex items-center gap-3 shrink-0">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Bill List
              </h2>
              {selectedRows.length > 0 && (
                <button
                  onClick={() => setDeleteModalOpen(true)}
                  className={`${actionButtonClass} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}
                >
                  <RiDeleteBin6Line />
                </button>
              )}
            </div>
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start justify-start sm:justify-end gap-3 w-full sm:w-auto">
            {/* Filter UI */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="w-32">
                <DynamicSelect
                  options={filterOptions}
                  value={filterOptions.find((o) => o.value === filterType)}
                  onChange={(o) => setFilterType(o.value)}
                  isSearchable={false}
                  placeholder="Filter"
                />
              </div>
              {filterType === "daily" && (
                <>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="p-2 border rounded text-sm"
                  />{" "}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="p-2 border rounded text-sm"
                  />
                </>
              )}
              {filterType === "monthly" && (
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="p-2 border rounded text-sm"
                />
              )}
              {filterType === "yearly" && (
                <input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="p-2 border rounded text-sm w-24"
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-3 sm:w-auto w-full">
              <button
                onClick={() => {
                  setEditBillId(null);
                  setEditBillData(null);
                  setGenerateBillModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 px-4 py-2 text-sm"
              >
                + Hospital Bill
              </button>
              <ExportOptions
                data={bills}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Hospital Bill List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>

        <style>{`@media print { .print-hidden, .print-hidden-in-print { display: none !important; } .print-table { width: 100%; border-collapse: collapse; } .print-table th, .print-table td { border: 1px solid #000; padding: 8px; font-size: 12px; } }`}</style>

        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print" scope="col">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        disabled={bills.length === 0}
                        aria-label="Select all bills on this page"
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="selectAll"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  {headers.map((h, i) => (
                    <th
                      key={i}
                      className="text-left uppercase font-medium text-sm p-3"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="print-hidden-in-print text-left uppercase font-medium text-sm p-3">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={skeletonColumns}
                  />
                ) : bills.length === 0 ? (
                  <tr className="print-hidden">
                    <td colSpan={11} className="text-center py-5 text-gray-500">
                      No bills found.
                    </td>
                  </tr>
                ) : (
                  <>
                    {currentData.map((bill, index) => {
                      const isFullyPaid =
                        (bill.paidAmount || 0) >= (bill.netAmount || 0);
                      const isReleased =
                        bill.admissionStatus === "Released" ||
                        bill.status === "Released";

                      return (
                        <tr
                          key={`normal-${bill.id}`}
                          className="border-b hover:bg-gray-50 print-hidden"
                        >
                          <td
                            className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print"
                            role="cell"
                          >
                            <div className="relative left-3 -top-[2px]">
                              <input
                                type="checkbox"
                                id={`bill-${index}`}
                                className="hidden peer"
                                checked={selectedRows.includes(bill.id)}
                                onChange={() => handleRowSelect(bill.id)}
                                aria-label={`Select bill ${bill.billNumber || "unknown"}`}
                              />
                              <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                                <FaCheck className="text-slate-100" />
                              </span>
                              <label
                                htmlFor={`bill-${index}`}
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                              ></label>
                            </div>
                          </td>
                          <td className="p-3 text-sm">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="p-3 text-sm">
                            {bill.billNumber || "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {bill.patientName || ""}
                          </td>
                          <td className="p-3 text-sm">
                            {bill.billingDate
                              ? new Date(bill.billingDate).toLocaleDateString("en-GB")
                              : "-"}
                          </td>
                          <td className="p-3 text-sm">
                            {bill.discount?.toFixed(2) || "0.00"}
                          </td>
                          <td className="p-3 text-sm">
                            {bill.netAmount?.toFixed(2) || "0.00"}
                          </td>
                          <td className="p-3 text-sm">
                            {bill.paidAmount?.toFixed(2) || "0.00"}
                          </td>
                          <td className="p-3 text-sm">
                            {user?.name || "Unknown"}
                          </td>
                          <td className="p-3 text-sm">
                            {bill.authorizedBy || "N/A"}
                          </td>

                          {/* ACTIONS COLUMN */}
                          <td className="p-3 text-right print-hidden-in-print">
                            <button
                              ref={(el) =>
                                (dropdownTriggerRefs.current[bill.id] = el)
                              }
                              onClick={() =>
                                setActiveDropdown(
                                  activeDropdown === bill.id ? null : bill.id,
                                )
                              }
                              className="size-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500"
                            >
                              <BsThreeDotsVertical size={14} />
                            </button>

                            <SmartDropdown
                              isOpen={activeDropdown === bill.id}
                              triggerRef={{
                                current: dropdownTriggerRefs.current[bill.id],
                              }}
                              onClose={() => setActiveDropdown(null)}
                              options={{ width: 160, align: "right" }}
                            >
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    handleAddMoney(bill);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                >
                                  <FaMoneyBillWave
                                    className="text-green-600"
                                    size={14}
                                  />{" "}
                                  <span>Add Money</span>
                                </button>

                                <button
                                  onClick={() => {
                                    handleView(bill);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FaEye className="text-blue-500" /> View
                                  Details
                                </button>

                                <button
                                  onClick={() => {
                                    handlePrintBill(bill);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FaPrint className="text-blue-500" /> Print Bill
                                </button>

                                <button
                                  onClick={() => {
                                    handleEdit(bill);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FaEdit className="text-green-600" /> Edit
                                  Bill
                                </button>

                                {!isReleased && (
                                  <button
                                    onClick={() => {
                                      handleReleaseClick(bill);
                                      setActiveDropdown(null);
                                    }}
                                    disabled={!isFullyPaid}
                                    className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                      isFullyPaid
                                        ? "text-gray-700 hover:bg-green-50 hover:text-green-700 cursor-pointer"
                                        : "text-gray-400 cursor-not-allowed"
                                    }`}
                                    title={
                                      !isFullyPaid
                                        ? "Bill must be fully paid to release"
                                        : "Release Patient"
                                    }
                                  >
                                    <FaWalking
                                      className={
                                        isFullyPaid
                                          ? "text-green-600"
                                          : "text-gray-300"
                                      }
                                    />{" "}
                                    Release
                                  </button>
                                )}

                                <button
                                  onClick={() => {
                                    setSelectedRows([bill.id]);
                                    setDeleteModalOpen(true);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                                >
                                  <RiDeleteBin6Line className="text-red-500" />{" "}
                                  Delete
                                </button>
                              </div>
                            </SmartDropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination (Keep Existing) */}
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
              <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                  Records: {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
                  {totalRecords}
                </p>
                <nav aria-label="Pagination">
                  <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === 1 ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                        aria-disabled={currentPage === 1}
                      >
                        <MdKeyboardArrowLeft />
                      </button>
                    </li>
                    {totalPages <= 10 ? (
                      [...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                          <button
                            className={`table-pagination-item ${
                              currentPage === i + 1 ? "bg-M-primary-color text-white" : ""
                            }`}
                            onClick={() => handlePageChange(i + 1)}
                            aria-label={`Go to page ${i + 1}`}
                            aria-current={currentPage === i + 1 ? "page" : undefined}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))
                    ) : (
                      (() => {
                        const pages = [];
                        const firstPages = 5;
                        const lastPages = 3;
                        for (let i = 1; i <= Math.min(firstPages, totalPages); i++) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i ? "bg-M-primary-color text-white" : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }
                        if (totalPages > firstPages + lastPages) {
                          pages.push(
                            <li key="ellipsis" className="table-pagination-item">
                              ...
                            </li>
                          );
                        }
                        for (
                          let i = Math.max(firstPages + 1, totalPages - lastPages + 1);
                          i <= totalPages;
                          i++
                        ) {
                          pages.push(
                            <li key={i}>
                              <button
                                className={`table-pagination-item ${
                                  currentPage === i ? "bg-M-primary-color text-white" : ""
                                }`}
                                onClick={() => handlePageChange(i)}
                                aria-label={`Go to page ${i}`}
                                aria-current={currentPage === i ? "page" : undefined}
                              >
                                {i}
                              </button>
                            </li>
                          );
                        }
                        return pages;
                      })()
                    )}
                    <li>
                      <button
                        className={`table-pagination-item ${currentPage === totalPages ? "disabled" : ""}`}
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                        aria-disabled={currentPage === totalPages}
                      >
                        <MdKeyboardArrowRight />
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      <ConfirmActionModal
        isOpen={releaseModalOpen}
        onClose={() => {
          setReleaseModalOpen(false);
          setBillToRelease(null);
        }}
        onConfirm={confirmRelease}
        ids={billToRelease ? [billToRelease.id] : []}
        entityName="patient release"
        title="Confirm Patient Release"
        confirmText="Release"
        cancelText="Cancel"
        message={`Are you sure you want to release patient ${billToRelease?.patientName}? This will update the admission status to Released.`}
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedRows([]);
        }}
        onConfirm={() => handleDelete(selectedRows)}
        ids={selectedRows}
        entityName="bill"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <HospitalBillModal
        isOpen={generateBillModalOpen}
        onClose={() => {
          setGenerateBillModalOpen(false);
          setEditBillId(null);
          setEditBillData(null);
        }}
        onSave={async (newBill) => {
          await handleRefreshBills(newBill);
        }}
        billId={editBillId}
        initialData={editBillData}
      />
      <HospitalViewBillModal
        isOpen={viewBillModalOpen}
        onClose={() => {
          setViewBillModalOpen(false);
          setViewBillData(null);
        }}
        bill={viewBillData}
        onEdit={handleBillUpdate}
        onDelete={handleDelete}
      />
      {showPrintPreview && printData && (
        <div className="hospital-bill-list-print-preview">
          <PrintBillLayoutA5
            className="hospital-bill-list-print-content"
            title={printData.title}
            billInfo={printData.billInfo}
            table={printData.table}
            summary={printData.summary}
            footer={printData.signatureFooter}
          />
        </div>
      )}

      <HospitalAddPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPaymentBillData(null);
        }}
        bill={paymentBillData}
        onPaymentSuccess={handleRefreshBills}
      />
    </div>
  );
};

export default HospitalBill;
